import React from 'react';
import { ListFormShowContextProvider, useListFormShowContext } from './EditableListFormShowContext';
import { useFormContext, useFormShowContext } from 'framework/form';
import { useShowContext } from 'framework/show';
import { ListProvider } from '../../ListContext';
import { cloneDeep, get, isEqual, orderBy, set } from 'lodash';
import { useLocation } from 'react-router-dom';
import { type Filter } from '../../filter/FilterContext';
import { FilterCondition } from 'framework/types';
import StaticFilters from '../../filter/StaticFilters';
import useListLocalStorageStoring from '../../builder/useListLocalStorageStoring';
import useListParams from '../../builder/useListParams';

interface ListFormShowTemplateProps extends React.PropsWithChildren {
  listSource: string;
  rowNumber?: number;
  defaultFilter?: Filter;
  localStorageKey?: string;
}

const EditableListFormShowTemplate = ({
  listSource,
  rowNumber = 0,
  defaultFilter = {},
  children,
  localStorageKey: localStorageKeyProps,
}: ListFormShowTemplateProps) => {
  const location = useLocation();
  const localStorageKey = localStorageKeyProps || `${location.pathname}:${listSource}`;

  const { formSource: parentFormSource } = useListFormShowContext();

  const { params: initialParams, updateParamsInLocalStorage } = useListLocalStorageStoring({
    localStorageKey,
    defaultFilter,
  });

  const formSource = (parentFormSource && `${parentFormSource}.${rowNumber}.${listSource}`) || listSource;
  const formSourceInternal = formSource.replace(/\.(\d+)\./g, '[$1].');

  const { isEditMode } = useFormShowContext();
  const { data: viewModeData, setData } = useShowContext();
  const { data: editModeData, setFormData } = useFormContext();

  const {
    params: { filter, sort },
    filterBy,
    sortBy,
  } = useListParams({
    initialParams,
    defaultFilter,
    onParamsChangeCallbacks: [updateParamsInLocalStorage],
  });

  const addToList = (entity: any) => {
    setFormData?.(
      set(cloneDeep(editModeData || {}), formSourceInternal, [...get(editModeData, formSourceInternal, []), entity]),
    );
  };

  const updateInList = (updatedEntity: any) => {
    setFormData?.(
      set(
        cloneDeep(editModeData || {}),
        formSourceInternal,
        get(editModeData, formSourceInternal, []).map((item: any) =>
          updatedEntity.id === item.id ? updatedEntity : item,
        ),
      ),
    );

    if (!isEditMode) {
      setData?.(
        set(
          cloneDeep(viewModeData || {}),
          listSource,
          get(viewModeData, listSource, []).map((item: any) => (updatedEntity.id === item.id ? updatedEntity : item)),
        ),
      );
    }
  };

  const deleteFromList = (id: StringOrNumber) => {
    setFormData?.(
      set(
        cloneDeep(editModeData || {}),
        formSourceInternal,
        get(editModeData, formSourceInternal, []).filter((item: any) => item.id !== id),
      ),
    );
  };

  const markForDeletion = (id: StringOrNumber) => {
    setFormData?.(
      set(
        cloneDeep(editModeData || {}),
        formSourceInternal,
        get(editModeData, formSourceInternal, []).map((item: any) =>
          item.id === id ? { ...item, _isMarkedForDeletion: true } : item,
        ),
      ),
    );
  };

  const undoMarkForDeletion = (id: StringOrNumber) => {
    setFormData?.(
      set(
        cloneDeep(editModeData || {}),
        formSourceInternal,
        get(editModeData, formSourceInternal, []).map((item: any) =>
          item.id === id ? { ...item, _isMarkedForDeletion: false } : item,
        ),
      ),
    );
  };

  const toTime = (v: any) => (v ? new Date(v).getTime() : NaN);

  const applyOperator = (fieldValue: any, operator: string, expectedValue: any) => {
    switch (operator) {
      case FilterCondition.EQ:
        return isEqual(fieldValue, expectedValue);
      case FilterCondition.IN: {
        if (Array.isArray(expectedValue) && Array.isArray(fieldValue)) {
          return expectedValue.every(v => fieldValue.includes(v));
        }
        return expectedValue.includes(fieldValue);
      }
      case FilterCondition.OR: {
        if (Array.isArray(expectedValue) && Array.isArray(fieldValue)) {
          return expectedValue.some(v => fieldValue.includes(v));
        }
        return expectedValue.includes(fieldValue);
      }
      case FilterCondition.GE:
        return !Number.isNaN(toTime(fieldValue)) && toTime(fieldValue) >= toTime(expectedValue);
      case FilterCondition.LE:
        return !Number.isNaN(toTime(fieldValue)) && toTime(fieldValue) <= toTime(expectedValue);
      default:
        return false;
    }
  };

  const filteredData = () => {
    let data = !isEditMode ? get(viewModeData, listSource) : get(editModeData, formSource);

    if (filter) {
      data =
        data?.map((item: any) => {
          if (!filter) return item;

          const isAcceptable = Object.entries(filter).every(([fieldName, rule]) => {
            const fieldValue = get(item, fieldName);
            return Object.entries(rule).every(([operator, expectedValue]) =>
              applyOperator(fieldValue, operator, expectedValue),
            );
          });

          return { ...item, _isFiltered: !isAcceptable };
        }) || [];
    }

    if (sort?.field) {
      data = orderBy(data || [], [sort.field], [sort.order === 'DESC' ? 'desc' : 'asc']);
    }

    return data || [];
  };

  const listContextValue = {
    data: {
      items: filteredData() as any[],
      totalPages: 0,
    },
    deleteFromList,
    markForDeletion,
    undoMarkForDeletion,
    updateInList,
    addToList,
    sort,
    sortBy,
    filter,
    filterBy,
    loading: false,
  };

  return (
    <ListFormShowContextProvider value={{ listSource, formSource }}>
      <ListProvider value={listContextValue}>
        <StaticFilters>{children}</StaticFilters>
      </ListProvider>
    </ListFormShowContextProvider>
  );
};

export default EditableListFormShowTemplate;
